import { Star } from "lucide-react";
import { brand } from "@/config/brand";

/**
 * GoogleRatingBadge — a compact "Google Reviews" rating badge.
 *
 * It is driven entirely by the `brand.googleReviews` config block in
 * src/config/brand.ts and renders NOTHING until you enable it there:
 *   • set `enabled: true`
 *   • fill in a real `profileUrl` (Google Business / reviews link)
 * While disabled (or with an empty URL) this component returns null, so it's
 * safe to leave mounted in the page.
 */
export default function GoogleRatingBadge({ className }: { className?: string }) {
  const { enabled, profileUrl, rating, reviewCount } = brand.googleReviews;

  // Hidden behind a single flag until the profile URL is provided.
  if (!enabled || !profileUrl) return null;

  const fg = "hsl(var(--foreground))";
  const fgMuted = "hsl(var(--muted-foreground))";

  return (
    <a
      href={profileUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-3 rounded-full px-5 py-2.5 transition-all hover:opacity-90 ${className ?? ""}`}
      style={{
        background: "var(--glass-bg)",
        backdropFilter: "blur(12px)",
        border: "1px solid var(--glass-border)",
        fontFamily: "Plus Jakarta Sans, sans-serif",
      }}
      aria-label={`${rating} out of 5 stars on Google Reviews${reviewCount ? ` from ${reviewCount} reviews` : ""}`}
    >
      {/* Google "G" logo */}
      <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.08 17.74 9.5 24 9.5z" />
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-3.59-13.46-8.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
      </svg>

      <span className="flex items-center gap-1.5">
        <span className="font-bold text-sm" style={{ fontFamily: "Outfit, sans-serif", color: fg }}>
          {rating.toFixed(1)}
        </span>
        <span className="flex items-center gap-0.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star
              key={i}
              size={13}
              fill={i < Math.round(rating) ? "hsl(45 100% 50%)" : "transparent"}
              style={{ color: "hsl(45 100% 50%)" }}
            />
          ))}
        </span>
      </span>

      <span className="text-xs font-semibold" style={{ color: fgMuted }}>
        Google Reviews{reviewCount ? ` (${reviewCount})` : ""}
      </span>
    </a>
  );
}
